import * as React from "react";
import { Icon, SmallIcon } from "@utdanningsdirektoratet/icon";
import { isEmpty } from "@utdanningsdirektoratet/utils/string";
import { onKeyDown } from "@utdanningsdirektoratet/utils/uu";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { IconButton } from "@utdanningsdirektoratet/button";
import { NotificationItem } from "./types";
import styles from "./Notification.module.less";

interface NotificationProps extends NotificationItem {
  id: string;
  onClose: () => void;
}

const Notification: React.FC<NotificationProps> = ({
  id,
  title,
  message,
  type,
  onClose,
  closeOnClick,
  autoClose = type === "success" && !message,
}) => {
  const [isClicked, setIsClicked] = React.useState(false);
  const closeTimeout = React.useRef<NodeJS.Timeout>();

  const onAnimationEnd = () => {
    if (isClicked) {
      onClose();
    }
  };

  const startCloseTimeout = (): void => {
    if (autoClose) {
      closeTimeout.current = setTimeout(() => {
        setIsClicked(true);
      }, 3000);
    }
  };

  const clearCloseTimeout = (): void => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
  };

  React.useEffect(() => {
    startCloseTimeout();

    return () => {
      clearCloseTimeout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getIcon = (): SmallIcon => {
    switch (type) {
      case "info":
        return "circle info";
      case "error":
        return "circle cross";
      case "success":
        return "circle check";
      default:
        return "question";
    }
  };

  const notificationClass = cls({
    [styles.notification]: true,
    [styles.notificationSlideOut]: isClicked,
    [styles.notificationGreen]: type === "success",
    [styles.notificationRed]: type === "error",
  });

  const onClick = (): void => setIsClicked(true);
  const onMouseOver = (): void => clearCloseTimeout();
  const onMouseOut = (): void => startCloseTimeout();

  return (
    <div // eslint-disable-line jsx-a11y/no-noninteractive-element-interactions
      className={notificationClass}
      onAnimationEnd={onAnimationEnd}
      role={type === "success" && !message ? "alert" : "alertdialog"}
      aria-labelledby={`${id}-title`}
      aria-describedby={!isEmpty(message) ? `${id}-message` : undefined}
      aria-live="polite"
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onFocus={onMouseOver}
      onBlur={onMouseOut}
      onClick={closeOnClick ? onClick : undefined}
      onKeyDown={closeOnClick ? onKeyDown(onClick) : undefined}
    >
      <div className={styles.notificationTitle}>
        <h3 id={`${id}-title`} className={cls(styles.notificationWrapper, styles.notificationTitle)}>
          <Icon className={styles.notificationIcon} icon={getIcon()} placement="before" />
          {title}
        </h3>
        {message && (
          <span className={styles.notificationMessage} id={`${id}-message`}>
            {message}
          </span>
        )}
      </div>
      <IconButton aria-label="Lukk melding" className={styles.notificationClose} onClick={onClick}>
        <Icon icon="cross" />
      </IconButton>
    </div>
  );
};

export default Notification;
